import React, { useEffect, useState } from 'react';
import { DcmSegmentMaster } from '../../../types/knowledge';

interface SegmentEditModalProps {
  segment: DcmSegmentMaster | null;
  personaCount: number;
  onClose: () => void;
  onSave: (segment: DcmSegmentMaster) => void;
  onDelete: (segmentId: string) => void;
}

// SegmentGenerateModal の textarea と同一の入力スタイル
const FIELD_CLASS =
  'box-border w-full rounded-[10px] border-[1.5px] border-[#E4E4E7] px-3 py-[10px] text-[12.5px] outline-none [font-family:inherit] focus:border-primary';
const LABEL_CLASS = 'mb-1 block text-[11px] font-bold text-[#6B7280]';

const SegmentEditModal: React.FC<SegmentEditModalProps> = ({
  segment,
  personaCount,
  onClose,
  onSave,
  onDelete,
}) => {
  const [draft, setDraft] = useState<DcmSegmentMaster | null>(segment);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  useEffect(() => {
    setDraft(segment);
    setIsConfirmingDelete(false);
  }, [segment]);

  if (!segment || !draft) return null;

  const updateDraft = (patch: Partial<DcmSegmentMaster>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const canSave = draft.segment_name.trim().length > 0;

  const renderTextarea = (
    id: string,
    label: string,
    value: string | undefined,
    rows: number,
    onChange: (next: string) => void,
  ) => (
    <div>
      <label className={LABEL_CLASS} htmlFor={id}>{label}</label>
      <textarea
        id={id}
        value={value || ''}
        rows={rows}
        onChange={(event) => onChange(event.target.value)}
        className={`${FIELD_CLASS} resize-y`}
      />
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(20,18,28,.42)] p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-[520px] overflow-hidden rounded-[18px] bg-white shadow-[0_30px_80px_rgba(0,0,0,.3)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="セグメントを編集"
      >
        <h3 className="flex items-center gap-2 px-[22px] pt-[18px] text-[16px] font-extrabold text-[#1D1927]">
          <span className="material-icons-round text-[19px] text-primary">view_week</span>
          セグメントを編集
        </h3>

        {isConfirmingDelete ? (
          <>
            <div className="px-[22px] pt-3 text-[13px] leading-[1.7] text-[#4A4D60]">
              <p className="mb-[14px] mt-[2px] text-[12px] leading-[1.6] text-[#5A5D6E]">
                このセグメントを削除しますか？
                {personaCount > 0 && `配下のペルソナ ${personaCount} 件も一緒に削除されます。`}
              </p>
            </div>
            <div className="flex justify-end gap-[9px] px-[22px] pb-5 pt-4">
              <button
                type="button"
                onClick={() => setIsConfirmingDelete(false)}
                className="inline-flex items-center gap-[5px] rounded-lg border border-[#E4E4E7] bg-white px-[11px] py-[5px] text-[12px] font-semibold text-[#4B5563] transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-[#4F5BB5]"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={() => onDelete(segment.segment_id)}
                className="inline-flex items-center gap-[6px] rounded-lg bg-destructive px-[13px] py-[6px] text-[13px] font-semibold text-white transition-colors hover:opacity-90"
              >
                <span className="material-icons-round text-[15px]" aria-hidden="true">delete_outline</span>
                削除する
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="max-h-[60vh] overflow-y-auto px-[22px] pt-3 text-[13px] leading-[1.7] text-[#4A4D60]">
              <div className="flex flex-col gap-[11px] pb-1">
                <div>
                  <label className={LABEL_CLASS} htmlFor="segment-edit-name">セグメント名</label>
                  <input
                    id="segment-edit-name"
                    type="text"
                    value={draft.segment_name}
                    onChange={(event) => updateDraft({ segment_name: event.target.value })}
                    className={FIELD_CLASS}
                  />
                </div>
                {renderTextarea('segment-edit-definition', '定義・特徴', draft.definition, 3, (next) =>
                  updateDraft({ definition: next }))}
                {renderTextarea('segment-edit-motivation', '動機', draft.motivation, 2, (next) =>
                  updateDraft({ motivation: next }))}
                {renderTextarea('segment-edit-situation', 'よくある状況', draft.common_situation, 2, (next) =>
                  updateDraft({ common_situation: next }))}
                {renderTextarea('segment-edit-desire', '共通の欲求', draft.common_desire, 2, (next) =>
                  updateDraft({ common_desire: next }))}
                {renderTextarea('segment-edit-anxiety', '共通の不安', draft.common_anxiety, 2, (next) =>
                  updateDraft({ common_anxiety: next }))}
                {renderTextarea(
                  'segment-edit-decision-context',
                  '意思決定の背景',
                  draft.common_decision_context,
                  2,
                  (next) => updateDraft({ common_decision_context: next }),
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-[9px] px-[22px] pb-5 pt-4">
              <button
                type="button"
                onClick={() => setIsConfirmingDelete(true)}
                className="inline-flex items-center gap-[5px] rounded-lg border border-transparent bg-white px-[11px] py-[5px] text-[12px] font-semibold text-destructive transition-colors hover:border-[#F0C9C9] hover:bg-[#FDF4F4]"
              >
                <span className="material-icons-round text-[15px]" aria-hidden="true">delete_outline</span>
                削除
              </button>
              <div className="flex gap-[9px]">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-[5px] rounded-lg border border-[#E4E4E7] bg-white px-[11px] py-[5px] text-[12px] font-semibold text-[#4B5563] transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-[#4F5BB5]"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={() => onSave(draft)}
                  disabled={!canSave}
                  className="inline-flex items-center gap-[6px] rounded-lg bg-primary px-[13px] py-[6px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="material-icons-round text-[15px]" aria-hidden="true">check</span>
                  保存
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SegmentEditModal;
